import { Button } from '@/components/ui/button'
import { FilterBox, SelectMultipleFilter } from '@/ui'
import type { SelectFilterOption } from '@/ui/rapid-ui/filters/select-filter'
import { useInvisibleInput } from '@/ui/rapid-ui/support'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type CampaignStatusFilter = 'active' | 'completed'

export type FiltersState = {
	from?: string
	to?: string
	status: readonly CampaignStatusFilter[]
	campaignIds: readonly string[]
}

export type FiltersBarProps = {
	value: FiltersState
	onChange: (patch: Partial<FiltersState>) => void
	onClear: () => void
	campaignOptions: ReadonlyArray<{ id: string; label: string }>
	campaignsLoading: boolean
	actions?: React.ReactNode
}

export function FiltersBar({
	value,
	onChange,
	onClear,
	campaignOptions,
	campaignsLoading,
	actions,
}: FiltersBarProps) {
	const { t } = useTranslation('analytics')
	const tf = (key: string) => t(`organisationAnalytics.filters.${key}`)
	const invisibleInputClassName = useInvisibleInput()

	const statusOptions: ReadonlyArray<SelectFilterOption<CampaignStatusFilter>> = [
		{ id: 'active', label: tf('status.active') },
		{ id: 'completed', label: tf('status.completed') },
	]

	const paddedCampaignOptions = useMemo<ReadonlyArray<SelectFilterOption<string>>>(
		() =>
			campaignOptions.map((c) => ({
				id: c.id,
				label: <span className="block pr-6">{c.label}</span>,
			})),
		[campaignOptions],
	)

	const hasActiveFilters = Boolean(value.from)
		|| Boolean(value.to)
		|| value.status.length > 0
		|| value.campaignIds.length > 0

	return (
		<div className="bg-white border border-grey-200 rounded-lg p-3">
			<div className="flex flex-wrap items-center gap-3">
				<FilterBox>
					<span className="pl-2 pr-1 text-sm font-medium text-grey-500">{tf('from')}</span>
					<input
						type="date"
						value={value.from ?? ''}
						onChange={(e) => onChange({ from: e.target.value || undefined })}
						className={`${invisibleInputClassName} rounded-md text-sm p-2 bg-transparent`}
					/>
				</FilterBox>

				<FilterBox>
					<span className="pl-2 pr-1 text-sm font-medium text-grey-500">{tf('to')}</span>
					<input
						type="date"
						value={value.to ?? ''}
						onChange={(e) => onChange({ to: e.target.value || undefined })}
						min={value.from}
						className={`${invisibleInputClassName} rounded-md text-sm p-2 bg-transparent`}
					/>
				</FilterBox>

				<SelectMultipleFilter<CampaignStatusFilter>
					options={statusOptions}
					values={value.status}
					onChange={(next) => onChange({ status: next })}
					placeholder={tf('status.placeholder')}
				/>

				<SelectMultipleFilter<string>
					options={paddedCampaignOptions}
					values={value.campaignIds}
					onChange={(next) => onChange({ campaignIds: next })}
					placeholder={campaignsLoading ? tf('campaigns.loading') : tf('campaigns.placeholder')}
				/>

				{hasActiveFilters && (
					<Button
						variant="link"
						onClick={onClear}
						className="whitespace-nowrap"
					>
						{tf('clear')}
					</Button>
				)}

				{actions && <div className="ml-auto">{actions}</div>}
			</div>
		</div>
	)
}
